import {
  Alert, Box, Button, Chip, Divider, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { ChevronLeft, ChevronRight, OpenInNewOutlined, PlayArrowOutlined } from '@mui/icons-material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import PcfReportService from '../../../shared/services/pcf-report/pcf-report.service'
import { IPcfFigures, IPcfReport } from '../../../shared/services/pcf-report/pcf-report.model'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { DownloadStatusContext } from '../../../context/download-status/download-status'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../loading-page'
import { ImportStepHeader } from '../settings/data-import/import-data/steps/import-step-header'
import { FormTextInputDisplay } from '../../presentation-components/form-field-components/new-and-edit/form-text-input'
import { FormDateInputDisplay } from '../../presentation-components/form-field-components/new-and-edit/form-date-input'

const STEPS = ['Review footprint', 'Boundary and methodology', 'Sign off and generate']

const DOWNLOADED_FILES_PATH = '/settings/company-settings/downloaded-files'

const DRAFT_FIELDS = [
  'boundaryStatement',
  'methodologyNarrative',
  'dataSourcesNarrative',
  'signOffName',
  'signOffTitle',
  'signOffDate',
  'publicationDate',
]

const fmt = (value: number | null) => (value == null
  ? '—'
  : Number(value).toLocaleString('en-GB', { maximumFractionDigits: 4 }))

/**
 * Product Carbon Footprint statement wizard (2028 NHS milestone).
 *
 * The footprint is rolled up from the product's bill of materials — the first place in the platform
 * that does so. Components without a factor are listed rather than skipped, because their absence is
 * what makes a total a lower bound rather than a footprint.
 */
export const ProductFootprintReportPage = () => {
  const pcfReportService = useMemo(() => new PcfReportService(), [])
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const downloadStatusContext = useContext(DownloadStatusContext)

  const [report, setReport] = useState<IPcfReport | null>(null)
  const [figures, setFigures] = useState<IPcfFigures | null>(null)
  const [draft, setDraft] = useState<Record<string, any>>({})
  const [activeStep, setActiveStep] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  const showError = (message: string) => {
    userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
  }

  useEffect(() => {
    if (!id) return
    Promise.all([pcfReportService.getOne(id), pcfReportService.getFigures(id)])
      .then(([reportResponse, figuresResponse]) => {
        applyReport(reportResponse)
        setFigures(figuresResponse)
        const requestedStep = Number(searchParams.get('step'))
        setActiveStep(Number.isFinite(requestedStep) && requestedStep > 0
          ? Math.min(requestedStep, STEPS.length - 1)
          : 0)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setIsLoading(false)
        showError(err?.response?.data?.message || 'Could not load this product footprint statement')
      })
  }, [id])

  // Matches the Carbon Reduction Plan wizard: the steps scroll, so moving between them would
  // otherwise leave the user part-way down the next step.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  const applyReport = (response: IPcfReport) => {
    setReport(response)
    const next: Record<string, any> = {}
    for (const field of DRAFT_FIELDS) next[field] = (response as any)[field] ?? ''
    setDraft(next)
  }

  const handleDraftChange = (field: string, value: any) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
  }

  const handleNext = async () => {
    if (!report) return
    setIsSaving(true)
    try {
      const saved = await pcfReportService.update(report.id, draft as Partial<IPcfReport>)
      applyReport(saved)
      const nextStep = Math.min(activeStep + 1, STEPS.length - 1)
      setActiveStep(nextStep)
      setSearchParams({ step: String(nextStep) }, { replace: true })
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'There was an error, please try again')
    } finally {
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    const previousStep = Math.max(activeStep - 1, 0)
    setActiveStep(previousStep)
    setSearchParams({ step: String(previousStep) }, { replace: true })
  }

  const handleGenerate = async () => {
    if (!report) return
    setIsGenerating(true)
    try {
      const response = await pcfReportService.generate(report.id)
      applyReport(response.pcfReport)
      for (const fileDownload of response.fileDownloads) {
        downloadStatusContext.startDownloadTracking(fileDownload.id)
      }
      userNotificationsContext.openSnackbar({ open: true, message: 'Generating your product footprint statement', backgroundColor: 'text.primary' })
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'Could not generate the statement, please try again')
    } finally {
      setIsGenerating(false)
    }
  }

  const renderStepContent = (step: number) => {
    if (!report || !figures) return null

    if (step === 0) {
      return (
        <>
          <Typography variant='h6' sx={{ mb: 1 }}>{figures.product.name}</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
            {figures.calculationBasisText}
          </Typography>

          <Alert severity='info' sx={{ mb: 3 }}>{figures.provisionalFormatNotice}</Alert>

          {figures.reportedTotalKg == null
            ? <Alert severity='warning' sx={{ mb: 3 }}>{figures.reconciliationText}</Alert>
            : (
              <Paper variant='outlined' sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
                <Typography variant='h4' sx={{ color: 'primary.main' }}>
                  {fmt(figures.perUnitKg)} <Typography component='span' variant='body1' sx={{ color: 'text.secondary' }}>kgCO2e</Typography>
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  per unit of {figures.product.name}
                </Typography>
                {figures.product.measurementQuantity > 1 && (
                  <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                    Total for the declared quantity of {fmt(figures.product.measurementQuantity)}:{' '}
                    {fmt(figures.reportedTotalKg)} kgCO2e
                  </Typography>
                )}
              </Paper>
            )}

          {figures.reconciliationText && figures.reportedTotalKg != null && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
              {figures.reconciliationText}
            </Typography>
          )}

          {figures.missingFactorCount > 0 && (
            <Alert severity='warning' sx={{ mb: 3 }}>
              {figures.missingFactorCount} of {figures.componentCount} components have no emission factor
              recorded, so they contribute nothing to the total. This footprint is a lower bound until they
              are populated.
            </Alert>
          )}

          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Component</TableCell>
                  <TableCell align='right'>Quantity per product</TableCell>
                  <TableCell align='right'>Factor (kgCO2e per unit)</TableCell>
                  <TableCell align='right'>Contribution (kgCO2e)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {figures.componentLines.map((line, index) => (
                  <TableRow key={`${line.name}-${index}`}>
                    <TableCell>{line.name}</TableCell>
                    <TableCell align='right'>
                      {fmt(line.quantityPerProduct)}{line.measurementUnitName ? ` ${line.measurementUnitName}` : ''}
                    </TableCell>
                    <TableCell align='right'>
                      {line.kgCarbonEmission == null
                        ? <Chip size='small' color='warning' variant='outlined' label='No factor recorded' />
                        : fmt(line.kgCarbonEmission)}
                    </TableCell>
                    <TableCell align='right'>{fmt(line.contributionKg)}</TableCell>
                  </TableRow>
                ))}
                {figures.componentLines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>No components are recorded against this product.</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={3}>Bill of materials subtotal</TableCell>
                  <TableCell align='right'>{fmt(figures.componentsTotalKg)}</TableCell>
                </TableRow>
                {figures.additionalKg !== 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>Additional product-level emissions</TableCell>
                    <TableCell align='right'>{fmt(figures.additionalKg)}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontFamily: 'EffraMd', borderTop: 2, borderColor: 'primary.main' }}>
                    Total product footprint
                  </TableCell>
                  <TableCell align='right' sx={{ fontFamily: 'EffraMd', borderTop: 2, borderColor: 'primary.main' }}>
                    {fmt(figures.calculatedTotalKg)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </>
      )
    }

    if (step === 1) {
      return (
        <>
          <Typography variant='h6' sx={{ mb: 1 }}>Boundary and methodology</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
            A product footprint is only meaningful alongside the life-cycle stages it covers. This cannot
            be inferred from the recorded factors, so state it here.
          </Typography>
          <Box sx={{ mb: 3 }}>
            <FormTextInputDisplay
              isEditPage={false}
              disabled={false}
              title='Life-cycle boundary'
              name='boundaryStatement'
              value={draft.boundaryStatement || ''}
              onChange={(event: any) => handleDraftChange('boundaryStatement', event.target.value)}
              multiline
              rows={3}
              forceOutlined
              regularFieldLabel
              helperText={figures.boundaryPrompt}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <FormTextInputDisplay
              isEditPage={false}
              disabled={false}
              title='Methodology'
              name='methodologyNarrative'
              value={draft.methodologyNarrative || ''}
              onChange={(event: any) => handleDraftChange('methodologyNarrative', event.target.value)}
              multiline
              rows={4}
              forceOutlined
              regularFieldLabel
              helperText='Optional. Any standard followed, allocation approach, or assumptions made.'
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <FormTextInputDisplay
              isEditPage={false}
              disabled={false}
              title='Data sources'
              name='dataSourcesNarrative'
              value={draft.dataSourcesNarrative || ''}
              onChange={(event: any) => handleDraftChange('dataSourcesNarrative', event.target.value)}
              multiline
              rows={4}
              forceOutlined
              regularFieldLabel
              helperText='Optional. Where the component emission factors came from: supplier data, EPDs, or a published database.'
            />
          </Box>
        </>
      )
    }

    return (
      <>
        <Typography variant='h6' sx={{ mb: 1 }}>Declaration and sign off</Typography>
        <Paper variant='outlined' sx={{ p: 3, mb: 4, backgroundColor: 'background.default' }}>
          {figures.declarationParagraphs.map((paragraph, index) => (
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }} key={index}>
              {paragraph}
            </Typography>
          ))}
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormTextInputDisplay
              isEditPage={false}
              disabled={false}
              title='Signed on behalf of the supplier by'
              name='signOffName'
              value={draft.signOffName || ''}
              onChange={(event: any) => handleDraftChange('signOffName', event.target.value)}
              forceOutlined
              regularFieldLabel
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextInputDisplay
              isEditPage={false}
              disabled={false}
              title='Job title'
              name='signOffTitle'
              value={draft.signOffTitle || ''}
              onChange={(event: any) => handleDraftChange('signOffTitle', event.target.value)}
              forceOutlined
              regularFieldLabel
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormDateInputDisplay
              isEditPage={false}
              disabled={false}
              title='Sign-off date'
              name='signOffDate'
              value={draft.signOffDate ? moment(draft.signOffDate) : null}
              onChange={(value: any) => handleDraftChange('signOffDate', value ? moment(value).format('YYYY-MM-DD') : null)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormDateInputDisplay
              isEditPage={false}
              disabled={false}
              title='Publication date'
              name='publicationDate'
              value={draft.publicationDate ? moment(draft.publicationDate) : null}
              onChange={(value: any) => handleDraftChange('publicationDate', value ? moment(value).format('YYYY-MM-DD') : null)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Button
          variant='contained'
          startIcon={<PlayArrowOutlined sx={{ fontSize: 'small' }} />}
          onClick={handleGenerate}
          disabled={isGenerating || figures.reportedTotalKg == null}
          sx={{ textTransform: 'none' }}
        >
          <Typography sx={{ pl: 0.5 }}>
            {isGenerating ? 'Generating…' : (report.status === 'GENERATED' ? 'Regenerate statement' : 'Generate statement')}
          </Typography>
        </Button>

        {report.status === 'GENERATED' && report.generatedAt && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              Last generated {moment(report.generatedAt).format('DD/MM/YYYY [at] HH:mm')}. Your PDF, web page and
              spreadsheet are in Settings → Downloaded Files.
            </Typography>
            {/* New tab so a part-completed statement is not lost by navigating away. */}
            <Button
              variant='outlined'
              component='a'
              href={DOWNLOADED_FILES_PATH}
              target='_blank'
              rel='noopener noreferrer'
              startIcon={<OpenInNewOutlined sx={{ fontSize: 'small' }} />}
              sx={{ textTransform: 'none' }}
            >
              <Typography sx={{ pl: 0.5 }}>View downloaded files</Typography>
            </Button>
          </Box>
        )}

        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 4 }}>
          {figures.disclaimerText}
        </Typography>
      </>
    )
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title='Product Carbon Footprint'
        includeBackButton={true}
        backButtonLink={report ? `/products/${report.finishedProductId}` : '/products'}
        backButtonText='Back to product'
        hasTabs={false}
      />
      <LoadingPage pageReady={!isLoading}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Box sx={{ width: '100%', maxWidth: 880 }}>
            <ImportStepHeader currentStep={activeStep + 1} totalSteps={STEPS.length} />
            <Box sx={{ mb: 4 }}>{renderStepContent(activeStep)}</Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant='outlined'
                onClick={handleBack}
                startIcon={<ChevronLeft />}
                disabled={activeStep === 0}
                sx={{ minWidth: 100, textTransform: 'none' }}
              >
                <Typography>Back</Typography>
              </Button>
              {activeStep < STEPS.length - 1 && (
                <Button
                  variant='contained'
                  onClick={handleNext}
                  disabled={isSaving}
                  endIcon={<ChevronRight />}
                  sx={{ minWidth: 100, textTransform: 'none' }}
                >
                  <Typography>{isSaving ? 'Saving…' : 'Next'}</Typography>
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </LoadingPage>
    </PageOuterContainer>
  )
}
